import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import back4app2 from '../back4app2';
import type { User } from '@back4app2/sdk';
import { AuthenticationError, AuthorizationError, NetworkError, PARSE_APP_DATABASE } from '@back4app2/sdk';
import { BACK4APP_DOT_COM_SITE_URL } from '../settings';
import { extractParseAppOptions, ParseAppOption } from '../utils/parseApps';
import AgentUpgradeModal from '../components/AIAgentComp/AgentUpgradeModal';
import { handleMessageErrors } from '../utils/handleMessageErrors';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES & CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

type AppChoice = 'create' | 'link' | 'none';

/**
 * Wizard phases:
 * - agent-form: Step 1 — Enter agent name and choose app option
 * - app-create: Step 2a — Enter app name before creating
 * - app-link: Step 2b — Select an existing app to link
 * - creating: Step 3a — Creating app (progress animation)
 * - linking: Step 3b — Linking app (spinner)
 * - success: Final — Backend ready, show "Get started"
 */
type WizardPhase =
  | 'agent-form'
  | 'app-create'
  | 'app-link'
  | 'creating'
  | 'linking'
  | 'success';

/**
 * Progress stages during app creation:
 * - creating-app: Parse app is being created
 * - connecting: Agent is being connected to the app
 * - done: All steps complete
 */
type CreateProgress = 'creating-app' | 'connecting' | 'done';

// Minimum time to show the linking screen (for the "link existing" flow)
const MIN_VALUE_SCREEN_MS = 3000;

function sleep(ms: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

// ─────────────────────────────────────────────────────────────────────────────
// SUBCOMPONENTS — Icons
// ─────────────────────────────────────────────────────────────────────────────

/** Database/Backend icon (cylinder) */
function DatabaseIcon({ className = '' }: { className?: string }) {
  return (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={className}
    >
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M21 5v14c0 1.66-4.03 3-9 3s-9-1.34-9-3V5" />
      <path d="M21 12c0 1.66-4.03 3-9 3s-9-1.34-9-3" />
    </svg>
  );
}

/** Checkmark icon for success state */
function CheckIcon({ className = '' }: { className?: string }) {
  return (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      className={className}
    >
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SUBCOMPONENTS — Header Banner
// ─────────────────────────────────────────────────────────────────────────────

interface ContextualHeaderProps {
  agentName?: string;
  visible: boolean;
  isComplete?: boolean;
}

/**
 * Contextual header that appears after the agent is created.
 * Shows "✓ Agent 'name' created" to give context in subsequent wizard steps.
 * When complete, shows "✓ Agent and App connected".
 * Uses a subtle gradient and glassmorphism for a polished look.
 */
function ContextualHeader({ agentName, visible, isComplete }: ContextualHeaderProps) {
  if (!visible) return null;

  return (
    <div className="mb-6 flex items-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-r from-[#27AE60]/15 to-[#27AE60]/5 border border-[#27AE60]/20 backdrop-blur-sm">
      <div className="w-6 h-6 rounded-full bg-[#27AE60]/20 flex items-center justify-center shadow-[0_0_12px_rgba(39,174,96,0.3)]">
        <CheckIcon className="w-3.5 h-3.5 text-[#27AE60]" />
      </div>
      <span className="text-sm text-[#27AE60] font-medium tracking-wide">
        {isComplete ? 'Agent and App connected' : `Agent ${agentName ? `"${agentName}"` : ''} created`}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

/**
 * CreateAgent — Multi-step wizard for creating an agent and optionally linking an app.
 *
 * Flow options:
 * - Create new app → shows progress → success
 * - Link existing app → shows app list → linking → success
 * - Don't link now → goes directly to agent chat
 */
export default function CreateAgent() {
  const navigate = useNavigate();
  const { user } = useOutletContext<{ user?: User }>();

  // ─── Wizard State ────────────────────────────────────────────────────────
  const [phase, setPhase] = useState<WizardPhase>('agent-form');

  // ─── Agent Form State ────────────────────────────────────────────────────
  const [agentName, setAgentName] = useState('');
  const [appChoice, setAppChoice] = useState<AppChoice>('create');
  const [isCreatingAgent, setIsCreatingAgent] = useState(false);
  const [isAppsOverLimit, setIsAppsOverLimit] = useState(false);
  const [hasNoApps, setHasNoApps] = useState(false);

  // ─── Created Agent ───────────────────────────────────────────────────────
  const [createdAgentId, setCreatedAgentId] = useState<string | undefined>(undefined);
  const [createdAgentName, setCreatedAgentName] = useState<string | undefined>(undefined);

  // ─── App Create State ────────────────────────────────────────────────────
  const [newAppName, setNewAppName] = useState('');
  const [createProgress, setCreateProgress] = useState<CreateProgress>('creating-app');
  const [isBackendReady, setIsBackendReady] = useState(false);

  // ─── App Link State ──────────────────────────────────────────────────────
  const [appOptions, setAppOptions] = useState<ParseAppOption[]>([]);
  const [selectedAppId, setSelectedAppId] = useState<string | undefined>(undefined);
  const [appSearch, setAppSearch] = useState('');
  const [isLoadingApps, setIsLoadingApps] = useState(false);

  // ─── Errors ──────────────────────────────────────────────────────────────
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [showUpgradePlanLink, setShowUpgradePlanLink] = useState(false);

  // Upgrade modal (same modal used by AgentInput / MyAgents)
  const [isAgentUpgradeModalOpen, setIsAgentUpgradeModalOpen] = useState(false);
  const [billingAgentId, setBillingAgentId] = useState<string | undefined>(undefined);

  // ─── Derived Values ──────────────────────────────────────────────────────
  const trimmedAgentName = useMemo(() => agentName.trim(), [agentName]);
  const trimmedNewAppName = useMemo(() => newAppName.trim(), [newAppName]);

  const defaultNewAppName = useMemo(() => {
    if (!trimmedAgentName) return 'my-app';
    return `${trimmedAgentName.toLowerCase().replace(/\s+/g, '-')}-app`;
  }, [trimmedAgentName]);

  const filteredApps = useMemo(() => {
    const s = appSearch.trim().toLowerCase();
    if (!s) return appOptions;
    return appOptions.filter(
      (o) => o.appName.toLowerCase().includes(s) || o.dashboardId.toLowerCase().includes(s)
    );
  }, [appOptions, appSearch]);


  // Show contextual header after agent is created (in app phases)
  const showContextualHeader =
    !!createdAgentId &&
    (phase === 'app-create' ||
      phase === 'app-link' ||
      phase === 'creating' ||
      phase === 'linking' ||
      phase === 'success');

  // ─── Effects ─────────────────────────────────────────────────────────────

  // Pre-check: verify apps count for both "no apps" and "over limit" conditions
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const raw = await back4app2.getParseApps();
        const toObject = (item: unknown): any | undefined => {
          if (!item) return undefined;
          if (typeof item === 'string') {
            try {
              return JSON.parse(item);
            } catch {
              return undefined;
            }
          }
          if (typeof item === 'object') return item as any;
          return undefined;
        };

        // Only Parse apps should affect this wizard (Back4App /listApps is heterogeneous).
        const parseAppsRaw = (Array.isArray(raw) ? raw : []).filter((item) => {
          const obj = toObject(item);
          // If we can't decode the payload, keep it to avoid false "no apps" (soft fallback).
          if (!obj) return true;
          return obj.type === 'parse';
        });

        // Total Parse apps (owner + collaborator) -> controls "Link existing app" availability
        const totalParseAppsCount = extractParseAppOptions(parseAppsRaw).length;

        // Owned Parse apps only -> controls maxApps limit check
        const ownedParseAppsRaw = parseAppsRaw.filter((item) => {
          const obj = toObject(item);
          // If we can't decode, we can't safely assert ownership; exclude from owned count.
          if (!obj) return false;
          return obj.type === 'parse' && obj.isCollab === false;
        });
        const ownedParseAppsCount = extractParseAppOptions(ownedParseAppsRaw).length;

        if (cancelled) return;

        // Check if user has no apps (disable "Link existing app")
        const noApps = totalParseAppsCount === 0;
        setHasNoApps(noApps);

        // If link is selected but no apps available, fall back to create or none
        if (noApps) {
          setAppChoice((prev) => (prev === 'link' ? 'create' : prev));
        }

        // Check if over limit (disable "Create new app")
        const maxApps = user?.maxApps;
        if (maxApps && maxApps > 0) {
          const overLimit = ownedParseAppsCount >= maxApps;
          setIsAppsOverLimit(overLimit);

          // If create is selected but not allowed, fall back to link or none
          if (overLimit) {
            setAppChoice((prev) => (prev === 'create' ? (noApps ? 'none' : 'link') : prev));
          }
        }
      } catch (e) {
        // If we can't pre-check, don't block the user; issues will surface later with proper messages.
        console.error('[CreateAgent] failed to pre-check apps', e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.maxApps]);

  // Set default app name when entering create phase
  useEffect(() => {
    if (phase === 'app-create' && !newAppName) {
      setNewAppName(defaultNewAppName);
    }
  }, [phase, newAppName, defaultNewAppName]);

  // Load apps when entering link phase
  useEffect(() => {
    if (phase !== 'app-link') return;

    setIsLoadingApps(true);
    setErrorMessage(undefined);
    setSelectedAppId(undefined);
    setAppSearch('');

    (async () => {
      try {
        const raw = await back4app2.getParseApps();
        const toObject = (item: unknown): any | undefined => {
          if (!item) return undefined;
          if (typeof item === 'string') {
            try {
              return JSON.parse(item);
            } catch {
              return undefined;
            }
          }
          if (typeof item === 'object') return item as any;
          return undefined;
        };
        const parseAppsRaw = (Array.isArray(raw) ? raw : []).filter((item) => {
          const obj = toObject(item);
          // Keep unknown/undecodable entries to avoid hiding apps due to unexpected payload shapes.
          if (!obj) return true;
          return obj.type === 'parse';
        });
        const parsed = extractParseAppOptions(parseAppsRaw);
        setAppOptions(parsed);
      } catch (e) {
        if (e instanceof NetworkError) {
          setErrorMessage('Network error while loading apps. Please try again.');
        } else if (e instanceof AuthenticationError || e instanceof AuthorizationError) {
          window.location.replace(
            `${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`
          );
          return;
        } else {
          console.error('Error loading parse apps', e);
          setErrorMessage('Unexpected error while loading apps.');
        }
      } finally {
        setIsLoadingApps(false);
      }
    })();
  }, [phase]);

  // Derived: creating phase is fully done (ready to proceed)
  const isCreateComplete = phase === 'creating' && createProgress === 'done' && isBackendReady;

  // ─── Handlers ────────────────────────────────────────────────────────────

  const resetErrors = useCallback(() => {
    setErrorMessage(undefined);
    setShowUpgradePlanLink(false);
  }, []);

  const handleOpenUpgradePlanModal = useCallback(async () => {
    try {
      // We need an existing agent id for billing context (same pattern as MyAgents).
      if (!billingAgentId) {
        const agents = await back4app2.findAgents('V3');
        const id = agents?.[0]?.id;
        if (id) setBillingAgentId(id);
      }
    } catch (err) {
      console.error('Failed to load agents for upgrade modal', err);
    }

    setIsAgentUpgradeModalOpen(true);
  }, [billingAgentId]);

  /**
   * Step 1: Create the agent and move to the appropriate next phase
   */
  const handleCreateAgent = useCallback(async () => {
    resetErrors();

    if (!trimmedAgentName) {
      setErrorMessage('Please enter an agent name.');
      return;
    }

    setIsCreatingAgent(true);
    try {
      const agent = await back4app2.createAgent(trimmedAgentName, 'V3');
      setCreatedAgentId(agent.id);
      setCreatedAgentName(agent.name || trimmedAgentName);

      // Navigate to next phase based on app choice
      if (appChoice === 'none') {
        navigate(`/agents/${agent.id}`);
        return;
      }

      if (appChoice === 'create') {
        setPhase('app-create');
        return;
      }

      setPhase('app-link');
    } catch (e) {
      const handled = handleMessageErrors(e);

      if ('redirectToLogin' in handled && handled.redirectToLogin) {
        window.location.replace(
          `${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`
        );
        return;
      }

      setShowUpgradePlanLink(Boolean(handled.showUpgradePlanLink));
      setErrorMessage(handled.message);
    } finally {
      setIsCreatingAgent(false);
    }
  }, [navigate, resetErrors, trimmedAgentName, appChoice]);

  /**
   * Step 2a: Start creating the app
   * The stepper animation runs for ~5s while backend work happens in parallel.
   * Button is enabled when BOTH animation completes AND backend is ready.
   */
  const handleStartCreateApp = useCallback(async () => {
    if (!createdAgentId) {
      setErrorMessage('Missing agent identifier. Please go back and try again.');
      return;
    }

    if (!trimmedNewAppName) {
      setErrorMessage('Please enter an app name.');
      return;
    }

    resetErrors();
    setCreateProgress('creating-app');
    setIsBackendReady(false);
    setPhase('creating');

    try {
      // Get parse versions to determine which version to use
      const versions = await back4app2.listParseVersion();
      const parseVersion =
        versions?.parseVersionList?.[0]?.version ||
        versions?.parseVersionList?.[0]?.id ||
        'latest';

      // Check if captcha is required
      const needsCaptcha = Boolean(versions?.showCaptcha);
      if (needsCaptcha) {
        throw new Error(
          'Captcha verification is required to create an app. Please complete signup verification and try again.'
        );
      }

      // Create the Parse app with MongoDB (NOSQL)
      const created = await back4app2.createParseApp(
        trimmedNewAppName,
        false, // isPublic
        String(parseVersion),
        null, // reCaptcha
        PARSE_APP_DATABASE.NOSQL
      );

      const dashboardId = created?.id;
      if (!dashboardId) {
        throw new Error('App creation did not return an id. Please try again.');
      }

      // Move to connecting stage
      setCreateProgress('connecting');

      // Link app to agent — backend runs deterministic sync
      await back4app2.setAgentCurrentParseApp(createdAgentId, dashboardId, true);

      // All done
      setCreateProgress('done');
      setIsBackendReady(true);
    } catch (e) {
      if (e instanceof NetworkError) {
        setErrorMessage('Network error while creating your app. Please check your connection and try again.');
      } else if (e instanceof AuthenticationError || e instanceof AuthorizationError) {
        window.location.replace(
          `${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`
        );
        return;
      } else {
        console.error('Error creating app', e);
        setErrorMessage((e as any)?.message || 'Unexpected error while creating app. Please try again.');
      }
      // Go back to create form on error
      setPhase('app-create');
    }
  }, [createdAgentId, resetErrors, trimmedNewAppName]);

  /**
   * Step 2b: Start linking the selected app
   */
  const handleStartLinkApp = useCallback(async () => {
    if (!createdAgentId) {
      setErrorMessage('Missing agent identifier. Please go back and try again.');
      return;
    }

    if (!selectedAppId) {
      setErrorMessage('Please select an app to link.');
      return;
    }

    resetErrors();
    setPhase('linking');

    try {
      // Run minimum delay in parallel with the link operation
      const minDelay = sleep(MIN_VALUE_SCREEN_MS);
      const work = back4app2.setAgentCurrentParseApp(createdAgentId, selectedAppId);
      await Promise.all([minDelay, work]);

      setPhase('success');
    } catch (e) {
      if (e instanceof NetworkError) {
        setErrorMessage('Network error while linking app. Please try again.');
      } else if (e instanceof AuthenticationError || e instanceof AuthorizationError) {
        window.location.replace(
          `${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`
        );
        return;
      } else {
        console.error('Error linking app', e);
        setErrorMessage((e as any)?.message || 'Unexpected error while linking app.');
      }
      // Go back to link selection on error
      setPhase('app-link');
    }
  }, [createdAgentId, resetErrors, selectedAppId]);

  /**
   * Navigate to the created agent
   */
  const handleGetStarted = useCallback(() => {
    if (createdAgentId) {
      navigate(`/agents/${createdAgentId}`);
    }
  }, [createdAgentId, navigate]);

  /**
   * Skip app association and go to agent
   */
  const handleSkip = useCallback(() => {
    if (createdAgentId) {
      navigate(`/agents/${createdAgentId}`);
    }
  }, [createdAgentId, navigate]);

  /**
   * Cancel and go back to agents list
   */
  const handleCancel = useCallback(() => {
    navigate('/agents');
  }, [navigate]);

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="w-full min-h-[100svh] sm:min-h-full flex items-start sm:items-center justify-center px-6 pb-12 overflow-y-auto">
      <div className="w-full my-6 sm:my-0 max-w-2xl h-fit bg-[#1e293b]/90 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
        {/* Contextual header (visible after agent is created) */}
        <ContextualHeader agentName={createdAgentName} visible={showContextualHeader} isComplete={isCreateComplete} />

        {/* ─── PHASE: Agent Form ─────────────────────────────────────────── */}
        {phase === 'agent-form' && (
          <>
            <h1 className="font-sora font-semibold text-2lg text-white">Create your Agent</h1>
            <p className="mt-2 text-sm text-white/60">
              Your Agent will power a scalable web app in the cloud. We'll help you set everything up step by step.
            </p>

            {/* Agent name input */}
            <div className="mt-6">
              <label className="text-sm text-light-blue font-medium">Agent name</label>
              <input
                value={agentName}
                onChange={(e) => setAgentName(e.target.value)}
                placeholder="e.g. support-agent, analytics-agent"
                className="mt-2 w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#208AEC]/50 transition-colors disabled:opacity-50"
                disabled={isCreatingAgent}
              />
            </div>

            {/* App choice */}
            <div className="mt-6">
              <label className="text-sm text-light-blue font-medium">How should this Agent run?</label>

              <div className="mt-3 flex flex-col gap-2">
                {/* Create new */}
                <button
                  type="button"
                  onClick={() => {
                    if (isCreatingAgent || isAppsOverLimit) return;
                    setAppChoice('create');
                  }}
                  aria-disabled={isCreatingAgent || isAppsOverLimit}
                  className={`w-full text-left px-4 py-3 rounded-xl border transition-colors ${
                    appChoice === 'create'
                      ? 'bg-[#208AEC]/10 border-[#208AEC]/40'
                      : 'bg-white/5 border-white/10 hover:bg-white/10'
                  } ${isCreatingAgent || isAppsOverLimit ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-white/90">
                        Create a new App <span className="text-[#27AE60]">(recommended)</span>
                      </div>
                      <div className="text-xs text-white/50 mt-1">
                        {"We'll create a new cloud App and connect it to your Agent automatically."}
                      </div>
                      {isAppsOverLimit && (
                        <div className="text-xs mt-2">
                          <span className="text-red-400">Exceeded the maximum number of apps.</span>{' '}
                        </div>
                      )}
                    </div>
                    <div
                      className={`w-4 h-4 min-w-4 min-h-4 shrink-0 rounded-full border ${
                        appChoice === 'create' ? 'bg-[#208AEC] border-[#208AEC]' : 'border-white/30'
                      }`}
                    />
                  </div>
                </button>

                {/* Link existing */}
                <button
                  type="button"
                  onClick={() => {
                    if (isCreatingAgent || hasNoApps) return;
                    setAppChoice('link');
                  }}
                  aria-disabled={isCreatingAgent || hasNoApps}
                  className={`w-full text-left px-4 py-3 rounded-xl border transition-colors ${
                    appChoice === 'link'
                      ? 'bg-[#208AEC]/10 border-[#208AEC]/40'
                      : 'bg-white/5 border-white/10 hover:bg-white/10'
                  } ${isCreatingAgent || hasNoApps ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-white/90">Connect an existing App</div>
                      <div className="text-xs text-white/50 mt-1">
                        Use an App you already have to run this Agent and access its data.
                      </div>
                      {hasNoApps && (
                        <div className="text-xs mt-2">
                          <span className="text-red-400">{"You don't have any App to connect."}</span>
                        </div>
                      )}
                    </div>
                    <div
                      className={`w-4 h-4 min-w-4 min-h-4 shrink-0 rounded-full border ${
                        appChoice === 'link' ? 'bg-[#208AEC] border-[#208AEC]' : 'border-white/30'
                      }`}
                    />
                  </div>
                </button>

                {/* Don't link now */}
                <button
                  type="button"
                  onClick={() => setAppChoice('none')}
                  disabled={isCreatingAgent}
                  className={`w-full text-left px-4 py-3 rounded-xl border transition-colors ${
                    appChoice === 'none'
                      ? 'bg-[#208AEC]/10 border-[#208AEC]/40'
                      : 'bg-white/5 border-white/10 hover:bg-white/10'
                  } ${isCreatingAgent ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-white/90">{"Don't connect an App now"}</div>
                    </div>
                    <div
                      className={`w-4 h-4 min-w-4 min-h-4 shrink-0 rounded-full border ${
                        appChoice === 'none' ? 'bg-[#208AEC] border-[#208AEC]' : 'border-white/30'
                      }`}
                    />
                  </div>
                </button>
              </div>
            </div>

            {/* Error message */}
            {errorMessage && (
              <div className="mt-5 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl p-3">
                <span>{errorMessage}</span>
                {showUpgradePlanLink && (
                  <>
                    {' '}
                    <span
                      className="underline text-[#208AEC] cursor-pointer hover:text-white transition-colors"
                      role="link"
                      tabIndex={0}
                      onClick={handleOpenUpgradePlanModal}
                      onKeyDown={(ev) => {
                        if (ev.key === 'Enter' || ev.key === ' ') {
                          handleOpenUpgradePlanModal();
                        }
                      }}
                    >
                      Upgrade Plan
                    </span>
                  </>
                )}
              </div>
            )}

            {/* Action buttons — always visible */}
            <div className="mt-8 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleCancel}
                disabled={isCreatingAgent}
                className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateAgent}
                disabled={isCreatingAgent || !trimmedAgentName}
                className="px-4 py-2 rounded-xl text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: (isCreatingAgent || !trimmedAgentName) ? '#374151' : '#27AE60' }}
                onMouseEnter={(e) => { if (!isCreatingAgent && trimmedAgentName) e.currentTarget.style.backgroundColor = '#19875F'; }}
                onMouseLeave={(e) => { if (!isCreatingAgent && trimmedAgentName) e.currentTarget.style.backgroundColor = '#27AE60'; }}
              >
                {isCreatingAgent ? 'Creating…' : 'Create Agent'}
              </button>
            </div>
          </>
        )}

        {/* ─── PHASE: App Create (Form) ──────────────────────────────────── */}
        {phase === 'app-create' && (
          <>
            <div className="flex flex-col items-center">
              {/* Database icon with subtle glow */}
              <div className="w-16 h-16 rounded-full bg-[#208AEC]/15 flex items-center justify-center shadow-[0_0_20px_rgba(32,138,236,0.2)]">
                <DatabaseIcon className="text-[#208AEC]" />
              </div>

              <h2 className="mt-4 text-xl font-semibold text-white text-center font-sora tracking-tight">
                Create your App
              </h2>

              <p className="mt-2 text-sm text-white/60 text-center max-w-lg leading-relaxed">
                {"Choose a name for your App. We'll create a cloud app with data storage, APIs, authentication, and hosting."}
              </p>
            </div>

            <div className="mt-6">
              <label className="text-sm text-light-blue font-medium">App name</label>
              <input
                value={newAppName}
                onChange={(e) => setNewAppName(e.target.value)}
                className="mt-2 w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-center placeholder-white/30 focus:outline-none focus:border-[#208AEC]/50 transition-colors"
                placeholder="my-app"
              />
            </div>

            {/* Error message */}
            {errorMessage && (
              <div className="mt-5 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl p-3">
                {errorMessage}
              </div>
            )}

            {/* Action buttons — always visible */}
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleSkip}
                className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 transition-colors"
              >
                Skip
              </button>
              <button
                type="button"
                onClick={handleStartCreateApp}
                disabled={!trimmedNewAppName}
                className="px-4 py-2 rounded-xl text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: !trimmedNewAppName ? '#374151' : '#27AE60' }}
                onMouseEnter={(e) => { if (trimmedNewAppName) e.currentTarget.style.backgroundColor = '#19875F'; }}
                onMouseLeave={(e) => { if (trimmedNewAppName) e.currentTarget.style.backgroundColor = '#27AE60'; }}
              >
                Create App
              </button>
            </div>
          </>
        )}

        {/* ─── PHASE: App Link (Selection) ───────────────────────────────── */}
        {phase === 'app-link' && (
          <>
            <div className="flex flex-col items-center">
              {/* Database icon with subtle glow */}
              <div className="w-16 h-16 rounded-full bg-[#208AEC]/15 flex items-center justify-center shadow-[0_0_20px_rgba(32,138,236,0.2)]">
                <DatabaseIcon className="text-[#208AEC]" />
              </div>

              <h2 className="mt-4 text-xl font-semibold text-white text-center font-sora tracking-tight">
                Link existing backend
              </h2>

              <p className="mt-2 text-sm text-white/60 text-center max-w-lg leading-relaxed">
                Select an existing backend to link with this agent. This may take ~10–30 seconds.
              </p>
            </div>

            {/* Search input */}
            <div className="mt-6">
              <input
                value={appSearch}
                onChange={(e) => setAppSearch(e.target.value)}
                placeholder="Search by app name…"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#208AEC]/50 transition-colors"
                disabled={isLoadingApps}
              />
            </div>

            {/* App list */}
            <div className="mt-4 max-h-72 overflow-y-auto glass-scroll pr-1">
              {isLoadingApps ? (
                <div className="text-sm text-white/50 py-6 text-center">Loading apps…</div>
              ) : filteredApps.length === 0 ? (
                <div className="text-sm text-white/50 py-6 text-center">
                  No apps found. You can create a new one instead.
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {filteredApps.map((o) => {
                    const selected = selectedAppId === o.dashboardId;
                    return (
                      <button
                        key={o.dashboardId}
                        type="button"
                        onClick={() => setSelectedAppId(o.dashboardId)}
                        className={`w-full text-left px-4 py-3 rounded-xl border transition-colors ${
                          selected ? 'bg-[#208AEC]/10 border-[#208AEC]/40' : 'bg-white/5 border-white/10 hover:bg-white/10'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-white/90 truncate">{o.appName}</div>
                            <div className="text-xs text-white/40 truncate">{o.dashboardId}</div>
                          </div>
                          <div
                            className={`w-4 h-4 rounded-full border ${
                              selected ? 'bg-[#208AEC] border-[#208AEC]' : 'border-white/30'
                            }`}
                          />
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Error message */}
            {errorMessage && (
              <div className="mt-4 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl p-3">
                {errorMessage}
              </div>
            )}

            {/* Action buttons — always visible */}
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleSkip}
                className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 transition-colors"
              >
                Skip
              </button>
              <button
                type="button"
                onClick={handleStartLinkApp}
                disabled={!selectedAppId || isLoadingApps}
                className="px-4 py-2 rounded-xl text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: (!selectedAppId || isLoadingApps) ? '#374151' : '#27AE60' }}
                onMouseEnter={(e) => { if (selectedAppId && !isLoadingApps) e.currentTarget.style.backgroundColor = '#19875F'; }}
                onMouseLeave={(e) => { if (selectedAppId && !isLoadingApps) e.currentTarget.style.backgroundColor = '#27AE60'; }}
              >
                Link
              </button>
            </div>
          </>
        )}

        {/* ─── PHASE: Creating (Progress) ────────────────────────────────── */}
        {phase === 'creating' && (
          <>
            <div className="flex flex-col items-center">
              {/* Center icon - spinner or check with glow */}
              <div
                className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-500 ${
                  isCreateComplete
                    ? 'bg-[#27AE60]/20 shadow-[0_0_40px_rgba(39,174,96,0.4)]'
                    : 'bg-[#208AEC]/15 shadow-[0_0_25px_rgba(32,138,236,0.3)]'
                }`}
              >
                {isCreateComplete ? (
                  <CheckIcon className="text-[#27AE60] transform scale-100 animate-[scaleIn_0.3s_ease-out]" />
                ) : (
                  <div className="w-8 h-8 rounded-full border-[3px] border-[#208AEC]/30 border-t-[#208AEC] animate-spin" />
                )}
              </div>

              {/* Dynamic headline */}
              <h2 className="mt-4 text-xl font-semibold text-white text-center font-sora tracking-tight">
                {isCreateComplete
                  ? 'Your web app is ready 🎉'
                  : createProgress === 'connecting'
                  ? 'Almost ready…'
                  : 'Setting up your app…'}
              </h2>

              {/* Dynamic subheadline */}
              <p className="mt-2 text-sm text-white/60 text-center max-w-lg leading-relaxed">
                {isCreateComplete
                  ? 'Your Agent is now running on a cloud App — ready to store data and scale automatically.'
                  : createProgress === 'connecting'
                  ? 'Just finishing the setup.'
                  : "We're preparing everything your app needs to run in the cloud."}
              </p>
            </div>

            {/* Progress Checklist — the protagonist */}
            <div className="mt-8 mx-auto max-w-sm">
              <div className="bg-[#0d1420]/60 border border-white/10 rounded-xl p-4 space-y-3">
                {/* Step 1: Agent created — always done */}
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#27AE60]/20 flex items-center justify-center flex-shrink-0">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="text-[#27AE60]">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  </div>
                  <span className="text-sm text-white font-medium">
                    Agent {createdAgentName ? `"${createdAgentName}"` : ''} created
                  </span>
                </div>

                {/* Step 2: Creating App */}
                <div className="flex items-center gap-3">
                  {createProgress === 'creating-app' ? (
                    // In progress - spinner
                    <div className="w-6 h-6 rounded-full bg-[#208AEC]/20 flex items-center justify-center flex-shrink-0">
                      <div className="w-3.5 h-3.5 rounded-full border-2 border-[#208AEC]/30 border-t-[#208AEC] animate-spin" />
                    </div>
                  ) : (
                    // Done - checkmark
                    <div className="w-6 h-6 rounded-full bg-[#27AE60]/20 flex items-center justify-center flex-shrink-0 animate-[scaleIn_0.3s_ease-out]">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="text-[#27AE60]">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    </div>
                  )}
                  <span className={`text-sm font-medium ${createProgress === 'creating-app' ? 'text-white' : 'text-white'}`}>
                    {createProgress === 'creating-app' ? 'Creating App' : 'App created'}
                  </span>
                </div>

                {/* Step 3: Connecting Agent and App */}
                <div className="flex items-center gap-3">
                  {createProgress === 'creating-app' ? (
                    // Pending - empty circle
                    <div className="w-6 h-6 rounded-full bg-white/5 border border-white/20 flex items-center justify-center flex-shrink-0">
                      <div className="w-2 h-2 rounded-full bg-white/20" />
                    </div>
                  ) : createProgress === 'connecting' ? (
                    // In progress - spinner
                    <div className="w-6 h-6 rounded-full bg-[#208AEC]/20 flex items-center justify-center flex-shrink-0">
                      <div className="w-3.5 h-3.5 rounded-full border-2 border-[#208AEC]/30 border-t-[#208AEC] animate-spin" />
                    </div>
                  ) : (
                    // Done - checkmark
                    <div className="w-6 h-6 rounded-full bg-[#27AE60]/20 flex items-center justify-center flex-shrink-0 animate-[scaleIn_0.3s_ease-out]">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="text-[#27AE60]">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    </div>
                  )}
                  <span className={`text-sm font-medium ${
                    createProgress === 'creating-app' ? 'text-white/40' : 'text-white'
                  }`}>
                    {createProgress === 'done' ? 'Agent and App connected' : 'Connecting Agent and App'}
                  </span>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <div className="mt-8 flex items-center justify-center">
              <button
                type="button"
                onClick={handleGetStarted}
                disabled={!isBackendReady}
                aria-busy={!isBackendReady}
                className={`
                  px-7 py-3 rounded-xl text-white font-semibold tracking-wide
                  transition-all duration-200
                  min-w-[170px]
                  ${
                    isBackendReady
                      ? 'shadow-[0_0_20px_rgba(39,174,96,0.4)] hover:shadow-[0_0_30px_rgba(39,174,96,0.6)] transform hover:scale-[1.02]'
                      : 'opacity-60 cursor-not-allowed shadow-none'
                  }
                `}
                style={{ backgroundColor: isBackendReady ? '#27AE60' : '#374151' }}
                onMouseEnter={(e) => {
                  if (isBackendReady) e.currentTarget.style.backgroundColor = '#19875F';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isBackendReady ? '#27AE60' : '#374151';
                }}
              >
                {isBackendReady ? (
                  'Get started'
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Building…
                  </span>
                )}
              </button>
            </div>
          </>
        )}

        {/* ─── PHASE: Linking (Spinner) ──────────────────────────────────── */}
        {phase === 'linking' && (
          <>
            <div className="flex flex-col items-center">
              {/* Database icon with pulse effect */}
              <div className="w-16 h-16 rounded-full bg-[#208AEC]/15 flex items-center justify-center shadow-[0_0_25px_rgba(32,138,236,0.3)] animate-pulse">
                <DatabaseIcon className="text-[#208AEC]" />
              </div>

              <h2 className="mt-4 text-xl font-semibold text-white text-center font-sora tracking-tight">
                Linking your backend…
              </h2>

              <p className="mt-2 text-sm text-white/60 text-center max-w-lg leading-relaxed">
                Connecting your app to the agent. This may take a few seconds.
              </p>
            </div>

            {/* Spinner with glow */}
            <div className="mt-8 flex justify-center">
              <div className="w-12 h-12 rounded-full border-4 border-[#208AEC]/20 border-t-[#208AEC] animate-spin shadow-[0_0_15px_rgba(32,138,236,0.3)]" />
            </div>

            <div className="mt-6 text-xs text-white/40 text-center">
              Setting up Preview, Database, Cloud Code, Authentication, and Hosting…
            </div>
          </>
        )}

        {/* ─── PHASE: Success ────────────────────────────────────────────── */}
        {phase === 'success' && (
          <>
            <div className="flex flex-col items-center">
              {/* Success checkmark icon with glow */}
              <div className="w-16 h-16 rounded-full bg-[#27AE60]/20 flex items-center justify-center shadow-[0_0_40px_rgba(39,174,96,0.4)]">
                <CheckIcon className="text-[#27AE60]" />
              </div>

              <h2 className="mt-4 text-xl font-semibold text-white text-center font-sora tracking-tight">
                Your backend is ready!
              </h2>

              <p className="mt-2 text-sm text-white/60 text-center max-w-lg leading-relaxed">
                Your agent is now linked. Database, APIs, Auth, Functions, and Hosting are live. Preview will appear on the right panel.
              </p>
            </div>

            {/* Action button — with glow effect */}
            <div className="mt-8 flex items-center justify-center">
              <button
                type="button"
                onClick={handleGetStarted}
                className="px-7 py-3 rounded-xl text-white font-semibold tracking-wide shadow-[0_0_20px_rgba(39,174,96,0.4)] hover:shadow-[0_0_30px_rgba(39,174,96,0.6)] transform hover:scale-[1.02] transition-all duration-200"
                style={{ backgroundColor: '#27AE60' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#19875F'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#27AE60'; }}
              >
                Get started
              </button>
            </div>
          </>
        )}
      </div>

      <AgentUpgradeModal
        appId={billingAgentId}
        isOpen={isAgentUpgradeModalOpen}
        onClose={() => setIsAgentUpgradeModalOpen(false)}
      />
    </div>
  );
}
